import { cn } from "cn";
import type * as React from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../dialog/alert-dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../dialog/drawer";
import { useMediaQuery } from "../hooks/use-media-query";
import { Spinner } from "../spinner";

const buttonBase = cn(
  /* sorted */
  "inline-flex items-center justify-center gap-2",
  "h-8 px-4 py-1.5 min-w-20",
  "text-sm font-medium whitespace-nowrap",
  "rounded-full cursor-pointer outline-none",
  "transition-[color,box-shadow]",
  "disabled:pointer-events-none disabled:bg-bg-dark-min disabled:text-text-dark-min",
  "[&_svg]:pointer-events-none [&_svg]:shrink-0",
);

const toneClasses = {
  default: "bg-bg-primary-max text-text-light hover:bg-bg-primary-max-hover",
  danger: "bg-bg-negative-max text-text-light hover:bg-bg-negative-max-hover",
} as const;

const cancelClass = cn(
  /* sorted */
  "text-text-dark-max",
  "bg-bg-transparent border border-border-dark-low",
  "hover:bg-bg-dark-min",
  buttonBase,
);

/** `md` is the AlertDialogContent default, so it contributes no override. */
const SIZE_CLASSES = {
  xs: "sm:max-w-xs",
  sm: "sm:max-w-sm",
  md: undefined,
  lg: "sm:max-w-xl",
} as const;

/**
 * The design system's small alert (320 wide): the title sits 16px under the
 * top edge with the body 4px below it, and the footer is 12px-padded with the
 * two buttons sharing its width. Wider presets keep the app's `p-4` rhythm.
 */
const XS_TITLE_CLASS = "px-4 pt-4 pb-0";
const XS_DESCRIPTION_CLASS = "px-4 pt-1 pb-4";
const STRETCH_FOOTER_CLASS = "p-3 sm:justify-stretch";

export type ConfirmModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** "danger" renders a red confirm button; "default" renders the brand-primary button. */
  tone?: "default" | "danger";
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  /** Hide the cancel button (e.g. acknowledgements). */
  hideCancel?: boolean;
  /** Sync or async confirm handler. Caller controls close via `onOpenChange`/`isPending`. */
  onConfirm: () => void | Promise<void>;
  /** Caller-owned in-flight flag — shows a spinner and disables both buttons. */
  isPending?: boolean;
  /** Label shown on the confirm button while `isPending`. Defaults to `confirmLabel`. */
  pendingLabel?: React.ReactNode;
  /** Gate the confirm button (e.g. "type the name to confirm"). */
  confirmDisabled?: boolean;
  /** Extra body content rendered between the description and the footer. */
  children?: React.ReactNode;
  /** Desktop width preset. `xs` is the 320px alert; `lg` suits modals carrying terms rather than a prompt. */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * `end` hugs the buttons to the footer's right (the app default); `stretch`
   * gives cancel and confirm equal widths across the footer, the way the
   * design system draws its small alerts.
   */
  buttonLayout?: "end" | "stretch";
};

/**
 * Declarative confirm / destructive modal. Renders an AlertDialog on desktop
 * (no outside-click dismissal) and a Drawer on mobile (< 1024px). The caller owns
 * `open`/`onOpenChange`; `onConfirm` may be async with a caller-managed `isPending`.
 */
export function ConfirmModal({
  open,
  onOpenChange,
  title,
  description,
  tone = "default",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  hideCancel = false,
  onConfirm,
  isPending = false,
  pendingLabel,
  confirmDisabled = false,
  children,
  size = "md",
  buttonLayout = "end",
}: ConfirmModalProps) {
  const isMobile = !useMediaQuery("(min-width: 1024px)");
  const stretch = buttonLayout === "stretch";

  const handleConfirm = () => {
    void onConfirm();
  };

  const confirmButton = (
    <button
      type="button"
      disabled={isPending || confirmDisabled}
      onClick={handleConfirm}
      className={cn(buttonBase, toneClasses[tone], stretch && "flex-1")}
    >
      {isPending && <Spinner />}
      {isPending && pendingLabel != null ? pendingLabel : confirmLabel}
    </button>
  );

  const cancelButton = hideCancel ? null : (
    <button
      type="button"
      disabled={isPending}
      onClick={() => onOpenChange(false)}
      className={cn(cancelClass, stretch && "flex-1")}
    >
      {cancelLabel}
    </button>
  );

  const body =
    children != null ? <div className="px-4 pb-1">{children}</div> : null;

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent showCloseButton={false}>
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            {description != null && (
              <DrawerDescription className="w-full">
                {description}
              </DrawerDescription>
            )}
          </DrawerHeader>
          {body}
          <DrawerFooter>
            {confirmButton}
            {cancelButton}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={SIZE_CLASSES[size]}>
        <AlertDialogHeader>
          <AlertDialogTitle
            className={size === "xs" ? XS_TITLE_CLASS : undefined}
          >
            {title}
          </AlertDialogTitle>
          {description != null && (
            <AlertDialogDescription
              className={size === "xs" ? XS_DESCRIPTION_CLASS : undefined}
            >
              {description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {body}
        <AlertDialogFooter
          className={stretch ? STRETCH_FOOTER_CLASS : undefined}
        >
          {cancelButton}
          {confirmButton}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
